<template>
  <div
    v-editable="blok"
    class="button"
    :class="`button--${blok.position ?? 'left'}`"
    :aria-label="blok.title"
  >
    <Button :type="buttonType" @click.prevent="openButtonLink">{{ blok.title }}</Button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { Button } from '@/common/components/ui';
import { CommonRoutesName } from '@/router/constants/routesName';
import { StoryblokButtonBlok } from '@/features/storyblok/types';
import {
  extractStorySlugFromWardPath,
  resolveStoryblokLink,
} from '@/features/storyblok/utils/link';

const props = defineProps<{
  blok: StoryblokButtonBlok;
}>();

const buttonType = computed(() => (props.blok.style === 'standard' ? 'standard' : 'primary'));
const router = useRouter();
const buttonTarget = computed(() =>
  resolveStoryblokLink(props.blok.buttonLink, props.blok.linkType)
);

const openButtonLink = () => {
  if (!buttonTarget.value) {
    return;
  }

  if (buttonTarget.value.type === 'external') {
    window.open(buttonTarget.value.href, '_blank', 'noopener,noreferrer');
    return;
  }

  if (buttonTarget.value.type === 'storyblok') {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: buttonTarget.value.slug },
    });
    return;
  }

  const storySlugFromInternalPath = extractStorySlugFromWardPath(buttonTarget.value.path);
  if (storySlugFromInternalPath) {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: storySlugFromInternalPath },
    });
    return;
  }

  void router.push(buttonTarget.value.path);
};
</script>

<style scoped>
.button {
  display: flex;
  align-items: center;
  padding: 16px;
  background: var(--ion-background-color);
}
.button--left {
  justify-content: flex-start;
}
.button--right {
  justify-content: flex-end;
}
.button--center {
  justify-content: center;
}
</style>
